import type { FC } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import type { SportTypeSB } from '@mwl/core-lib';
import { getLinesSB, getOutrightsSB, useCancelRequest, useFilteredLinesBehavior } from '@mwl/core-lib';
import { GroupedLines } from '@/components/GroupedLines/GroupedLines';
import type { LinesLoadableLoad } from '@/components/LinesLoadable/LinesLoadable';
import { LinesLoadable } from '@/components/LinesLoadable/LinesLoadable';
import { useIsCyberSportPage } from '@/hooks';
import { sportLinesEnrichment } from '@/utils';
import type { LineWithEntities } from '@/types';
import { MessageEmptyLines } from '../MessageEmptyLines/MessageEmptyLines';
import type { FilteredLinesProps } from './FilteredLines.types';
const pageSize = 20;
const BaseFilteredLines: FC<FilteredLinesProps> = ({
  variant
}) => {
  const [loadFinished, setLoadFinished] = useState(false);
  const [isEmptyLines, setIsEmptyLines] = useState(false);
  const [pregames, setPregames] = useState<Array<LineWithEntities>>([]);
  const isCyberSportPage = useIsCyberSportPage();
  const cancelRequest = useCancelRequest();
  const {
    stage,
    upcoming,
    reverseStage,
    isOutrightStage,
    isShowOutrights,
    joinedIds,
    isMainPage
  } = useFilteredLinesBehavior();
  const sportTypes: Array<SportTypeSB> = useMemo(() => isCyberSportPage ? ['CYBER'] : ['CYBER', 'TRADITIONAL'], [isCyberSportPage]);
  const loadLines: LinesLoadableLoad = useCallback(async ({
    pageOffset
  }) => {
    setIsEmptyLines(false);
    setLoadFinished(false);
    const request = isOutrightStage ? getOutrightsSB : getLinesSB;
    const {
      sportIds,
      categoryIds,
      championshipIds
    } = joinedIds;
    const {
      status,
      data
    } = await request({
      pageOffset: pageOffset * pageSize,
      pageSize,
      stage,
      upcoming,
      sportIds,
      categoryIds,
      championshipIds,
      sportTypes
    });
    if (status === 200 && data) {
      return sportLinesEnrichment(data ?? []);
    }
    return [];
  }, [isOutrightStage, sportTypes, stage, upcoming, joinedIds]);
  const loadLinesWithReverseStage: LinesLoadableLoad = useCallback(async ({
    pageOffset
  }) => {
    const {
      sportIds,
      categoryIds,
      championshipIds
    } = joinedIds;
    const {
      status,
      data
    } = await getLinesSB({
      pageOffset: pageOffset * pageSize,
      pageSize,
      stage: reverseStage,
      upcoming,
      sportIds,
      categoryIds,
      championshipIds,
      sportTypes
    });
    if (status === 200 && data) {
      return sportLinesEnrichment(data ?? []);
    }
    return [];
  }, [joinedIds, reverseStage, sportTypes, upcoming]);
  const loadOutrightLines: LinesLoadableLoad = useCallback(async ({
    pageOffset
  }) => {
    const {
      sportIds,
      categoryIds,
      championshipIds
    } = joinedIds;
    const {
      status,
      data
    } = await getOutrightsSB({
      pageOffset: pageOffset * pageSize,
      pageSize,
      sportIds,
      categoryIds,
      championshipIds
    });
    if (status === 200 && data) {
      return sportLinesEnrichment(data ?? []);
    }
    return [];
  }, [joinedIds]);
  const loadPregames = useCallback(async () => {
    const {
      status,
      data
    } = await getLinesSB({
      pageOffset: 0,
      pageSize: isMainPage ? 12 : 3,
      sportIds: joinedIds.pregamesSportIds,
      stage: 'PREGAME',
      upcoming: 'IN_1_HOUR',
      sportTypes
    });
    if (!cancelRequest.current && status === 200 && data) {
      setPregames(sportLinesEnrichment(data ?? []));
    }
  }, [cancelRequest, joinedIds, isMainPage, sportTypes]);
  const handleLoadFinished = useCallback(() => {
    setLoadFinished(true);
    if (stage === 'LIVE') {
      loadPregames();
    }
  }, [loadPregames, stage]);
  const handleEmptyLines = (isLoadedEmptyLines: boolean) => {
    setIsEmptyLines(isLoadedEmptyLines);
  };
  return <>
      <LinesLoadable pageSize={pageSize} loadLines={loadLines} onLoadFinished={handleLoadFinished} onEmptyLines={handleEmptyLines} emptyMessage={<MessageEmptyLines />} variant={variant} data-sentry-element="LinesLoadable" data-sentry-source-file="FilteredLines.tsx" />
      {stage === 'LIVE' && loadFinished && !isEmptyLines && !!pregames.length && <GroupedLines lines={pregames} variant={variant} />}
      {stage && loadFinished && isEmptyLines && !isOutrightStage && <LinesLoadable pageSize={pageSize} loadLines={loadLinesWithReverseStage} variant={variant} />}
      {loadFinished && !isEmptyLines && isShowOutrights && <LinesLoadable pageSize={pageSize} loadLines={loadOutrightLines} variant="OUTRIGHT" />}
    </>;
};
export const FilteredLines = memo(BaseFilteredLines);
FilteredLines.displayName = 'FilteredLines';